<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { Ref } from 'vue'
  import { computed, reactive, ref } from '#imports'
  import { TaskBlockQuizData, TaskBlockTypeId } from '~/model/TaskBlockData/TaskBlockData'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import { GameTaskId } from '~/model/GameTask/GameTask'
  import { useGameTaskStore } from '~/store/GameTask'
  import useTranslation from '~/composables/Translation/Translation'
  import useNotification from '~/composables/Notification/Notification'
  import useBem from '~/composables/Bem/Bem'
  import useMarkdown from '~/composables/Markdown/Markdown'
  import { GameTaskStoreQuizEntry } from '~/model/GameTaskStore/GameTaskStore'
  import {
    QuizAnswerEntry,
    QuizQuestionEntry,
    QuizStoreAnswersDict,
  } from '~/model/TaskQuiz/TaskQuiz'
  import { OverlayContentModalFacet } from '~/components/OverlayContent/OverlayContentModal/OverlayContentModalFacet'
  import { OverlayContentModalAction } from '~/components/OverlayContent/OverlayContentModal/OverlayContentModalAction'
  import useModal from '~/composables/Modal/Modal'

  interface Props extends TaskBlockQuizData, UseBemProps {
    facets?: Array<string>
    taskId: GameTaskId
    type: TaskBlockTypeId
    title: string
    questionList: Array<QuizQuestionEntry>
  }

  const isMultipleChoice = (answerList: Array<QuizAnswerEntry>): boolean => {
    return answerList.filter(({ correct }) => correct).length > 1
  }

  const gameTaskStore = useGameTaskStore()
  const gameTaskStoreRefs = storeToRefs(gameTaskStore)

  const props = defineProps<Props>()
  const { t } = useTranslation()
  const { openModal, closeModal } = useModal()
  const { openNotification } = useNotification()
  const { bemAdd, bemFacets } = useBem('c-task-block-quiz', props, {})
  const { parseMarkdown } = useMarkdown()

  const store = gameTaskStoreRefs[props.taskId] as Ref<GameTaskStoreQuizEntry>
  const model = reactive<GameTaskStoreQuizEntry>({
    id: props.taskId,
    answers: props.questionList.reduce((acc, { id, answerList }) => {
      const answers = store.value.answers ?? {}
      acc[id] = answers[id] ?? (isMultipleChoice(answerList) ? [] : '')
      return acc
    }, {} as QuizStoreAnswersDict),
    isCompleted: store.value.isCompleted,
  })

  const rootClasses = computed<Array<string>>(() => {
    return [
      ...bemFacets.value,
      bemAdd(store.value.isCompleted ? 'is-completed' : ''),
      bemAdd(store.value.isCompleted ? 'is-completed' : '', '', 's-task-block'),
    ]
  })
  const activeQuestionIdx = ref<number>(0)
  const activeQuestion = computed<QuizQuestionEntry>(() => {
    return props.questionList[activeQuestionIdx.value]
  })
  const stepDisplay = computed<string>(() => {
    return `${activeQuestionIdx.value + 1} / ${props.questionList.length}`
  })

  const isPrevDisabled = computed<boolean>(() => {
    return activeQuestionIdx.value <= 0
  })

  const isNextDisabled = computed<boolean>(() => {
    const isLast = activeQuestionIdx.value >= props.questionList.length - 1
    const hasAnswer = hasAnswers(model.answers[activeQuestion.value.id])

    return isLast || !hasAnswer
  })

  const isCompletedDisabled = computed<boolean>(() => {
    return !hasAnswers(model.answers[activeQuestion.value.id])
  })

  const hasAnswers = (answers: string | Array<string>): boolean => {
    if (Array.isArray(answers)) {
      return answers.some(Boolean)
    }

    return !!answers
  }

  const checkSolution = (): boolean => {
    const result = props.questionList.every(({ id, answerList }) => {
      const answers = store.value.answers[id]
      const solution = answerList.find(({ correct }) => correct)

      if (isMultipleChoice(answerList)) {
        return answerList.every(({ id, correct }) => {
          const isIncluded = (answers as Array<string>).includes(id)
          return isIncluded === !!correct
        })
      }

      return (answers as string) === solution?.id
    })

    return result
  }

  const onRequestSave = () => {
    store.value.answers = { ...model.answers }
    store.value.isCompleted = model.isCompleted
  }

  const onRequestPrev = () => {
    onRequestSave()
    activeQuestionIdx.value = Math.max(0, activeQuestionIdx.value - 1)
  }

  const onRequestNext = () => {
    onRequestSave()
    activeQuestionIdx.value = Math.min(props.questionList.length - 1, activeQuestionIdx.value + 1)
  }

  const onRequestComplete = () => {
    onRequestSave()
    const isCorrect = checkSolution()

    if (isCorrect) {
      model.isCompleted = true
      onRequestSave()
      openNotification('overlay-notification-task-complete', 4000)
    } else {
      openModal('overlay-info-task-failed', {
        props: {
          facets: [OverlayContentModalFacet.Info],
          actions: <Array<OverlayContentModalAction>>[
            {
              callback: closeModal,
              label: t('overlay-info-task-failed.action-accept.label'),
              btnFacets: ['primary', 'medium'],
            },
          ],
        },
      })
    }
  }
</script>

<template>
  <div :class="rootClasses" class="c-task-block-quiz s-task-block">
    <div class="s-task-block__box">
      <TaskBlockHeader :is-completed="store.isCompleted" :title="title" :type="type" />

      <div class="c-task-block-quiz__interactions s-task-block__interactions">
        <div class="c-task-block-quiz__header">
          <h2>{{ activeQuestionIdx + 1 }}. {{ activeQuestion.title }}</h2>
        </div>
        <div class="c-task-block-quiz__answers">
          <CheckboxList
            v-if="isMultipleChoice(activeQuestion.answerList)"
            v-model="model.answers[activeQuestion.id]"
            :items="activeQuestion.answerList"
            class="c-task-block-quiz__answers-list"
          />
          <RadioList
            v-else
            v-model="model.answers[activeQuestion.id]"
            :items="activeQuestion.answerList"
            class="c-task-block-quiz__answers-list"
          />
        </div>
        <div class="c-task-block-quiz__actions">
          <button
            :disabled="isPrevDisabled ? true : null"
            class="btn btn--primary btn--medium"
            @click="onRequestPrev"
          >
            {{ t('task-block-quiz.actions.prev') }}
          </button>
          <span class="c-task-block-quiz__step">
            {{ stepDisplay }}
          </span>
          <button
            :disabled="isNextDisabled ? true : null"
            class="btn btn--primary btn--medium"
            @click="onRequestNext"
          >
            {{ t('task-block-quiz.actions.next') }}
          </button>
          <button
            v-if="activeQuestionIdx === questionList.length - 1 && !store.isCompleted"
            :disabled="isCompletedDisabled ? true : null"
            class="btn btn--primary btn--medium"
            @click="onRequestComplete"
          >
            {{ t('task-block-quiz.actions.complete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/base/typography/typography' as type;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;
  @use '/assets/scss/util/box' as box;

  .c-task-block-quiz {
    grid-column: 3 / 11;
  }

  .c-task-block-quiz--is-completed {
    .c-task-block-quiz__answers {
      pointer-events: none;
    }
  }

  .c-task-block-quiz__interactions {
    display: grid;
    gap: 32px;
  }

  .c-task-block-quiz__answers {
    display: grid;
    grid-template-areas: '. answers-area .';
    grid-template-columns: 1fr 6fr 1fr;
  }

  .c-task-block-quiz__answers-list {
    grid-area: answers-area;
  }

  .c-task-block-quiz__actions {
    display: flex;
    justify-content: flex-end;
    column-gap: 24px;
  }

  .c-task-block-quiz__step {
    @include type.h4;
    display: flex;
    align-items: center;
  }
</style>
